import type { ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { SampleKey } from '@tallyframe/core';
import { useAddSample } from '../api/onboarding-queries';
import { useSession } from '../app/session-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Dialog } from '../ui/Dialog';
import { Stack } from '../ui/Stack';

const SAMPLES: Array<{ key: SampleKey; title: string; description: string }> = [
  { key: 'agency-clients', title: 'Agency client report', description: 'Hours, budget and spend per client per month.' },
  { key: 'store-sales', title: 'Online store monthly sales', description: 'Orders, revenue and returns by product and channel.' },
  { key: 'studio-memberships', title: 'Fitness studio memberships', description: 'Sign-ups, cancellations and class attendance by location.' }
];

/**
 * Three sample reports to start from. "Add" and "Added" post the same request:
 * the server returns the existing report when the workspace already has it.
 */
export function SampleChooser(props: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  added: SampleKey[];
}): ReactElement {
  const router = useRouter();
  const workspaceId = useSession().snapshot.workspaceId ?? '';
  const addSample = useAddSample(workspaceId);

  function add(key: SampleKey): void {
    if (addSample.isPending) return;
    addSample.mutate(key, {
      onSuccess: (detail) => {
        void router.navigate({
          to: '/w/$workspaceId/reports/$reportId',
          params: { workspaceId, reportId: detail.report.id }
        });
      }
    });
  }

  return (
    <Dialog title="Start from a sample" size="md" isOpen={props.isOpen} onOpenChange={props.onOpenChange}>
      <Stack gap={3}>
        {SAMPLES.map((sample) => (
          <Card key={sample.key} title={sample.title}>
            <Stack gap={3}>
              <p className="m-0 p-0 text-md text-muted">{sample.description}</p>
              <Button
                variant="secondary"
                isDisabled={addSample.isPending}
                onPress={() => add(sample.key)}
              >
                {props.added.includes(sample.key) ? 'Added' : 'Add'}
              </Button>
            </Stack>
          </Card>
        ))}
        <ErrorBanner error={addSample.error} />
      </Stack>
    </Dialog>
  );
}
